/**
 * Base label dictionary for the whole app.
 * Keys are flat and dot-namespaced so a future i18n layer can consume them as-is:
 *   entity.*  -> domain nouns (singular/plural). Overriding these renames the concept everywhere.
 *   field.*   -> table column headers and form field labels shared across screens.
 *   title.*   -> page/section titles.
 *   action.*  -> buttons and commands.
 *   nav labels resolve to entity.*.plural so one override renames tab, titles and tables together.
 *
 * Per-deployment overrides live in GotaSettings.terminology (admin: Configuración > Terminología)
 * and win over these values at runtime. An unknown key renders the key itself, never blank.
 */
export const BASE_LABELS = {
	// --- Balance / estado de cuenta (same keys and defaults as config/documentLabels.js;
	// the business preset's documentLabels sit between these and the overrides) ---
	// Balance
	'balance.saldo.toPay': 'Saldo a pagar al propietario',
	'balance.saldo.toCollect': 'Saldo a cobrar al propietario',
	'balance.section.dues': 'Cuotas de deudas',
	'balance.total.dues': 'Total cuotas de deudas',
	'balance.empty.dues': 'No hay cuotas de deudas',
	'balance.section.bills': 'Gastos',
	'balance.empty.bills': 'Sin gastos este mes',
	'balance.total.ownerBills': 'Total gastos propietario',
	'balance.total.tenantCharges': 'Total cargos al inquilino',
	'balance.badge.tenantCharge': 'Cargo inquilino',
	'balance.badge.ownerCollects': 'Cobra propietario',
	'balance.row.ownerCollected': 'Cobrado por el propietario (no suma)',
	'balance.row.ivaIncluded': 'IVA incluido',
	'balance.row.ivaOnTop': 'IVA a facturar',
	// Estado de cuenta
	'statement.title': 'Estado de cuenta',
	'statement.debt.party': 'Cliente',
	'statement.credit.party': 'Beneficiario',
	'statement.col.due': 'Cuota',
	'statement.col.expiration': 'Vencimiento',
	'statement.col.amount': 'Importe',
	'statement.col.paid': 'Pagado',
	'statement.col.pending': 'Pendiente',
	'statement.row.net': 'Importe neto',
	'statement.row.total': 'Total',
	'statement.row.paid': 'Pagado',
	'statement.debt.pending': 'Saldo pendiente a abonar',
	'statement.credit.pending': 'Saldo a su favor',
	'statement.paymentInfo': 'Datos para el pago',
	// --- Domain entities ---
	'entity.account': 'Cuenta',
	'entity.account.plural': 'Cuentas',
	'entity.account.debtor': 'Deudor',
	'entity.account.debtor.plural': 'Deudores',
	'entity.account.creditor': 'Acreedor',
	'entity.account.creditor.plural': 'Acreedores',
	// Account roles derived from data (accounts search ?role=…; menu tabs Clientes/Proveedores/…)
	'entity.account.role.client': 'Cliente',
	'entity.account.role.client.plural': 'Clientes',
	'entity.account.role.supplier': 'Proveedor',
	'entity.account.role.supplier.plural': 'Proveedores',
	'entity.account.role.staff': 'Colaborador',
	'entity.account.role.staff.plural': 'Colaboradores',
	'entity.account.role.partner': 'Socio',
	'entity.account.role.partner.plural': 'Socios',
	'entity.debt': 'Deuda',
	'entity.debt.plural': 'Deudas',
	'entity.credit': 'Pasividad',
	'entity.credit.plural': 'Pasividades',
	'entity.contract': 'Alquiler',
	'entity.contract.plural': 'Alquileres',
	'entity.asset': 'Activo Fijo',
	'entity.asset.plural': 'Activos fijos',
	'entity.assetSale': 'Venta',
	'entity.assetSale.plural': 'Ventas',
	'entity.due': 'Cuota',
	'entity.due.plural': 'Cuotas',
	'entity.payment': 'Pago',
	'entity.payment.plural': 'Pagos',
	'entity.bill': 'Gasto',
	'entity.bill.plural': 'Gastos',
	'entity.contact': 'Contacto',
	'entity.contact.plural': 'Contactos',
	'entity.document': 'Documento',
	'entity.document.plural': 'Documentos',
	'entity.warranty': 'Garantía',
	'entity.warranty.plural': 'Garantías',
	'entity.tenant': 'Inquilino',
	'entity.assetOwner': 'Propietario',
	'entity.purchaser': 'Comprador',
	'entity.concept': 'Concepto',
	'entity.report.plural': 'Reportes',
	'entity.prediction.plural': 'Previsiones',

	// --- Navigation / dashboard ---
	'nav.dashboard': 'Inicio',
	'nav.reports': 'Reportes',
	'nav.predictions': 'Previsiones',
	// Menu groups (ObjectTab.group, SHOW_NAV_GROUPS): 'nav.group.<groupKey>'
	'nav.group.ventas': 'Ventas',
	'nav.group.compras': 'Compras',
	'nav.group.personal': 'Personal',
	'nav.group.finanzas': 'Finanzas',
	'nav.group.catalogo': 'Catálogo',

	// --- List filters ---
	// Accounts balance filters (accountType=deudores|acreedores): accounts with an
	// unpaid debt / credit due. Own keys so renaming the debtor/creditor nouns
	// (e.g. to Cliente/Proveedor) does not turn them into role filters.
	'filter.account.toCollect': 'Deudores',
	'filter.account.toPay': 'Acreedores',
	'filter.account.allRoles': 'Todos los roles',

	// --- Page / section titles ---
	'title.confirmDelete': 'Confirmar Eliminación',
	'title.debtConceptConfig': 'Configuración de conceptos de deuda',
	'title.debtConceptConfigDescription': 'Elegí qué conceptos de deuda mostrar y en qué orden.',
	'title.creditConceptConfig': 'Configuración de conceptos de pasividad',
	'title.creditConceptConfigDescription': 'Elegí qué conceptos de pasividad mostrar y en qué orden.',
	'title.tabConfig': 'Configuración de pestañas',
	'title.tabConfigDescription': 'Elegí qué pestañas mostrar en el navegador.',
	'title.contractTemplates': 'Plantillas de Contrato',

	// --- Shared field / column labels ---
	'field.name': 'Nombre',
	'field.type': 'Tipo',
	'field.currency': 'Moneda',
	'field.folder': 'Carpeta',
	'field.amount': 'Monto',
	'field.dueAmount': 'Monto cuota',
	'field.date': 'Fecha',
	'field.email': 'Email',
	'field.address': 'Dirección',
	'field.phone': 'Teléfono',
	'field.description': 'Descripción',
	'field.observations': 'Observaciones',
	'field.status': 'Estado',
	'field.id': 'ID',
	'field.capital': 'Capital',
	'field.endDate': 'Fecha vto',
	'field.expiration': 'Vto',
	'field.expirationDate': 'Vencimiento',
	'field.periodicity': 'Periodicidad',
	'field.accrual': 'Devengamiento',
	'field.arrearsDueDay': 'Vence el día (mes siguiente)',
	'field.interest': 'Interes',
	'field.interestPercent': '% Interés',
	'field.amortization': 'Amortización',
	'field.balance': 'Saldo',
	'field.withBalance': 'Con saldo',
	'field.settled': 'Saldada',
	'field.noFinancing': 'Sin financiación',
	'field.surcharge': 'Recargo',
	'field.unpaidAmount': 'Monto pendiente',
	'field.paid': 'Pago?',
	'field.active': 'Activo',
	'field.price': 'Precio/Valor',
	'field.ubication': 'Ubicación',
	'field.guaranteedAmount': 'Monto garantizado',
	'field.totalGuaranteed': 'Total Garantizado',
	'field.multipleLots': 'Multiples padrones?',
	'field.lot': 'Padron',
	'field.lotUpper': 'Padrón',
	'field.periodInit': 'Inicio',
	'field.periodEnd': 'Fin',
	'field.closeDate': 'Fecha Finalización',
	'field.previousBalance': 'Saldo Anterior',
	'field.previousBalanceDate': 'Fecha de saldo Anterior',
	'field.previousDebt': 'Deuda Anterior',
	'field.previousDebtDate': 'Fecha de Deuda Anterior',
	'field.details': 'detalles',
	'field.paymentMethod': 'Método de pago',
	'field.lastAdjustmentDate': 'Fecha último ajuste',
	'field.lastAdjustmentIpc': 'Porcentaje último ajuste',
	'field.lastPaymentDate': 'Fecha ultimo pago',
	'field.accountConcept': 'Cuenta:Concepto',
	'field.totalDebts': 'Total Deudas',
	'field.initialPayment': 'Pago Inicial',
	'field.option': 'Opción',
	'field.debtCurrency': 'Moneda Deuda',
	'field.assetCurrency': 'Moneda Activo',
	'field.contract': 'Contrato',
	'field.property': 'Propiedad',
	'field.companyCommission': 'Comision Inmobiliaria',
	'field.rut': 'RUT',
	'field.documentType': 'Tipo de documento',
	'field.documentNumber': 'Nro. de documento',
	'field.bankAccountUSD': 'Cuenta Bancaria USD',
	'field.bankAccountUYU': 'Cuenta Bancaria UYU',
	'field.irpf': 'IRPF',

	// --- Actions ---
	'action.save': 'Guardar',
	'action.saveChanges': 'Guardar cambios',
	'action.downloadPdf': 'Descargar PDF',
	'action.export': 'Exportar',
	'action.sendEmail': 'Enviar Email',
	'action.sendWhatsapp': 'Enviar WhatsApp',
	'action.cancel': 'Cancelar',
	'action.search': 'Buscar',
	'action.restore': 'Restaurar',
	'action.add': 'Agregar',
	'action.applySurcharge': 'Aplicar Recargo',
	'action.back': 'Atrás',
	'action.backToDebt': 'Volver a la deuda',
	'action.backToContract': 'Volver al alquiler',
	'action.backToAccount': 'Volver a la cuenta',
	'action.backToAsset': 'Volver al activo',
	'action.backToAssetSale': 'Volver a la venta',
	'action.calculate': 'Calcular',
	'action.confirm': 'Confirmar',
	'action.confirmUsers': 'Confirmar usuarios',
	'action.delete': 'Eliminar',
	'action.edit': 'Editar',
	'action.editStartDate': 'Editar fecha de inicio',
	'action.goToAssetSale': 'Ir a venta de activo',
	'action.goToContract': 'Ir a alquiler',
	'action.goToGenericContract': 'Ir a contrato',
	'action.refinanceDebt': 'Refinanciar deuda',
	'action.upload': 'Subir',
	'action.backPlain': 'Atras',
	'action.changeStartDate': 'Cambiar Fecha de inicio',
	'action.uploadLower': 'subir',
	'action.viewDetails': 'Ver Detalles',
	'action.viewReceipt': 'Ver Comprobante',

	// --- Extra field / column labels ---
	'field.lastCount': 'Último conteo',
	'field.monthTotal': 'Total del mes',
	'field.options': 'Opciones',
	'field.percentage': 'Porcentaje',
	'field.provisionalAmount': 'Monto provisorio',
	'field.creditDue': 'Cuota Pasividad',
	'field.debtDue': 'Cuota Deuda',
	'field.finishDate': 'Fecha de finalizacion',
	'field.linkedCredit': 'Pasividad vinculada',
	'field.previousCredit': 'Crédito Anterior',
	'field.previousCreditPlain': 'Credito Anterior',

	// --- Form labels / placeholders ---
	'form.billingType': 'Tipo de facturación',
	'form.billingType.fixed': 'Monto fijo',
	'form.billingType.perUser': 'Por usuario',
	'form.comprobante': 'Comprobante',
	'form.contractCloseDate': 'Fecha Finalizacion de Contrato',
	'form.debtStartDate': 'Fecha de inicio de deuda',
	'form.debtStartDateLong': 'Fecha de inicio de la deuda',
	'form.detail': 'Detalle',
	'form.dueMonth': 'mes de cuota',
	'form.endDate': 'Fecha de fin',
	'form.existingDataAction': 'Que hacer con los Datos existentes:',
	'form.firstDueDay': 'Día vencimiento de 1er cuota:',
	'form.firstDueDayLong': 'Día de vencimiento de primer cuota:',
	'form.generateFrom': 'Generar a partir de:',
	'form.hasPreviousDebt': 'Tiene deuda anterior?',
	'form.hasPreviousDebtQ': '¿Tiene deuda anterior?',
	'form.inHowManyMonths': 'En cuantos meses:',
	'form.initialUserCount': 'Cantidad inicial de usuarios',
	'form.interestPercent': 'Interes %',
	'form.lastConfirmedUserCount': 'Última cantidad confirmada de usuarios',
	'form.markPreviousDuesPaid': 'Marcar cuotas previas como pagas',
	'form.no': 'No',
	'form.numMonths': 'Ingrese en cuantos meses:',
	'form.originalAmount': 'Monto Original',
	'form.paidBy': 'Quien realiza el pago',
	'form.paidQ': 'Paga?',
	'form.paymentMedium': 'Medio de pago',
	'form.percentage': 'Porcentaje (%)',
	'form.periodicityMonths': 'Periodicidad en meses',
	'form.placeholder.payerName': 'Nombre del pagador',
	'form.placeholder.paymentMedium': 'Ej: Efectivo, Transferencia',
	'form.placeholder.searchAccount': 'Buscar Cuenta... ',
	'form.placeholder.searchAssetSale': 'Buscar una venta de activo fijo...',
	'form.placeholder.searchContract': 'Buscar un alquiler...',
	'form.placeholder.searchContractGeneric': 'Buscar un contrato...',
	'form.previousDebtDate': 'Fecha deuda anterior',
	'form.pricePerUser': 'Precio por usuario',
	'form.surchargeType': 'Tipo de Recargo',
	'form.userCountMonth': 'Cantidad de usuarios del mes',
	'form.yes': 'Sí',
	'form.chooseCurrency': 'Elige Moneda',
	'form.chooseLinkedCredit': 'Elige la Pasividad vinculada',
	'form.creditStartDate': 'Fecha de inicio de credito',
	'form.creditStartDateAccent': 'Fecha de inicio de crédito',
	'form.creditStartDateLa': 'Fecha de inicio de la crédito',
	'form.creditStartDateLong': 'Fecha de inicio del crédito',
	'form.hasPreviousCredit': 'Tiene crédito anterior?',
	'form.hasPreviousCreditPlain': 'Tiene credito anterior?',
	'form.placeholder.searchCredit': 'Buscar pasividad...',
	'form.placeholder.searchDebt': 'Buscar deuda...',
	'form.placeholder.searchDue': 'Buscar cuota...',
	'form.previousCreditDate': 'Fecha crédito anterior',
	'form.previousCreditDatePlain': 'Fecha credito anterior',

	// --- User-facing messages / toasts ---
	'msg.accountLoadFailed': 'No se pudo obtener cuenta/s',
	'msg.allDuesGenerated': 'Todas las cuotas fueron creadas',
	'msg.assetSaleCapitalExceeded': 'Error. La sumatoria del capital inicial y las deudas registradas no puede superar el valor de la venta del inmueble.',
	'msg.assetSaleLoadFailed': 'No se pudo obtener venta/s',
	'msg.calculatedSurcharge': 'Recargo calculado',
	'msg.completeFields': 'Debe completar el/los campo/s',
	'msg.conceptsLoadFailed': 'No se pudieron cargar los conceptos',
	'msg.confirmCountBeforePayment': 'Confirme la cantidad de usuarios del mes antes de registrar cobros',
	'msg.dueCancelledByClose': 'La cuota fue cancelada por la finalización del contrato; no admite cobros',
	'msg.confirmDeleteDebt': 'Seguro desea eliminar deuda? Si elimina deuda, seran eliminados todos los pagos relacionados',
	'msg.confirmDeletePayment': 'Seguro desea eliminar el pago?',
	'msg.confirmDeleteAccount': 'Seguro desea eliminar la cuenta?',
	'entity.interest.plural': 'Intereses',
	'msg.deleteDebtIntro': 'Se eliminarán definitivamente los siguientes registros de la deuda.',
	'msg.deleteDebtTypeId': 'Para confirmar, escriba',
	'msg.deleteDebtIdMismatch': 'El identificador no coincide.',
	'msg.deleteDebtDeleting': 'Eliminando la deuda y sus registros…',
	'msg.deleteDebtPreviewFailed': 'No se pudo obtener el detalle de la eliminación de la deuda',
	'msg.deleteDebtCollectedPayments': 'Se eliminarán {count} cobros por {total}. Cuotas pagadas: {paidDueCount}.',
	'msg.deleteDebtBlocked': 'Esta deuda no puede eliminarse. Revise los registros relacionados antes de continuar.',
	'msg.deleteDebtPreserved': 'Se conservarán',
	'msg.deleteDebtGenerated': 'Registros generados por el sistema',
	// Generic cascade deletion modal (contracts, credits, assets, sales, bank accounts)
	'msg.cascadeIntro': 'Se eliminarán definitivamente los siguientes registros.',
	'msg.cascadeEmpty': 'Solo se eliminará este registro; no tiene registros relacionados.',
	'msg.cascadeTypeLabel': 'Para confirmar, escriba',
	'msg.cascadeLabelMismatch': 'El texto no coincide.',
	'msg.cascadeDeleting': 'Eliminando registros…',
	'msg.cascadePreviewFailed': 'No se pudo obtener el detalle de la eliminación',
	'msg.cascadeCollectedPayments': 'Se eliminarán {count} cobros/pagos por {total}. Cuotas pagadas: {paidDueCount}.',
	'msg.cascadeBlocked': 'Este registro no puede eliminarse. Revise los registros relacionados antes de continuar.',
	'msg.cascadePreserved': 'Se conservarán',
	'msg.cascadeGenerated': 'Registros generados por el sistema',
	'msg.cascadeUnlinked': 'Se desvincularán (no se eliminan)',
	'msg.contractDeleteVsClose': 'Si el contrato terminó, use «Finalizar» en lugar de eliminar: finalizar lo cierra en una fecha, deja de generar cuotas nuevas y conserva el historial de cuotas, cobros y documentos. Eliminar lo borra definitivamente junto con todo lo listado abajo, y no se puede deshacer.',
	'action.closeContract': 'Finalizar contrato',
	'action.closeContractRetroactive': 'Finalizar y cancelar cuotas posteriores',
	'msg.closeContractIntro': 'El contrato quedará finalizado en la fecha elegida: dejan de generarse cuotas nuevas y se conserva todo su historial.',
	'action.regenerateContract': 'Regenerar contrato',
	'title.regenerateContract': 'Regenerar contrato',
	'msg.regenerateContractIntro': 'Esta deuda de alquiler no tiene contrato. Se creará uno con los datos de la deuda (inquilino, monto y moneda) en la propiedad que elija, y la deuda quedará vinculada. Si la deuda conserva el identificador del contrato eliminado, se reutiliza, y los registros que lo referenciaban vuelven a quedar vinculados. Luego puede editar el contrato normalmente.',
	'msg.regeneratingContract': 'Regenerando…',
	'msg.contractRegenerated': 'Contrato regenerado',
	'msg.bankAccountDeleteIntro': 'La cuenta bancaria se quitará de los registros que la usan. Ninguno de ellos se elimina.',
	'msg.bankAccountDeleted': 'Cuenta bancaria eliminada',
	'msg.bankAccountsLoadFailed': 'No se pudieron cargar las cuentas bancarias',
	'title.deleteBankAccount': 'Eliminar Cuenta Bancaria',
	'title.bankAccounts': 'Cuentas bancarias',
	'settings.item.bankAccounts': 'Cuentas bancarias',
	'entity.commission.plural': 'Comisiones',
	'field.bank': 'Banco',
	'field.branch': 'Sucursal',
	'field.accountType': 'Tipo de cuenta',
	'field.accountNumber': 'Número de cuenta',
	'field.accountHolder': 'Titular',
	'msg.deleteAccountIntro': 'Se eliminarán definitivamente los siguientes registros.',
	'msg.deleteAccountTypeName': 'Para confirmar, escriba',
	'msg.deleteAccountNameMismatch': 'El nombre no coincide.',
	'msg.deleteAccountBlocked': 'Esta cuenta no puede eliminarse porque hay registros de otras cuentas vinculados a sus activos o contratos. Resuelva estos casos antes de continuar.',
	'msg.deleteAccountPreserved': 'Se conservarán',
	'msg.deleteAccountGenerated': 'Registros generados por el sistema',
	'msg.deleteAccountDeleting': 'Eliminando la cuenta y sus registros…',
	'msg.deleteAccountPreviewFailed': 'No se pudo obtener el detalle de la eliminación',
	'msg.confirmDeleteContact': 'Seguro desea eliminar el contacto?',
	'msg.confirmDeleteDocument': 'Seguro desea eliminar este documento?',
	'msg.confirmDeleteEvent': '¿Eliminar el evento "{title}"?',
	'msg.confirmDeleteInterest': 'Seguro desea eliminar el interes?',
	'msg.confirmDeleteUser': 'Seguro desea eliminar el Usuario?',
	'msg.confirmDeleteCollection': 'Seguro desea eliminar el cobro?',
	'msg.confirmDeleteCommission': '¿Seguro desea eliminar esta comisión?',
	'msg.contractLoadFailed': 'No se pudo obtener contrato/s',
	'msg.debtCreated': 'Deuda creada',
	'msg.debtCreatedWithPreviousDues': 'Ésta deuda fue creada con fecha anterior, por lo que fueron generadas las cuotas previas a la fecha de creación',
	'msg.debtDeleted': 'Deuda Eliminada',
	'msg.debtExpired': 'Deuda vencida',
	'msg.debtStartDateTooltip': 'Es la fecha de inicio de la deuda, si usted selecciona una fecha anterior al día de hoy se crearán las cuotas pasadas, si no es lo que desea seleccione una fecha igual o mayor a la de hoy.',
	'msg.debtStartDateUpdated': 'Fecha de inicio de deuda actualizada correctamente',
	'msg.debtUpdated': 'Deuda actualizada Correctamente',
	'msg.dueAlreadyPaid': 'Esta cuota ya está pagada',
	'msg.dueCreated': 'Cuota creada',
	'msg.duePendingUntilCount': 'Cada cuota queda pendiente hasta confirmar la cantidad de usuarios del mes.',
	'msg.editDatesTooltip': 'Estos datos solo se pueden editar luego de creada la deuda mediante la opción:Cambiar fecha de inicio.',
	'msg.enterCapitalInterest': 'Debe ingresar Capital e interés',
	'msg.enterCapitalInterestDues': 'Debe ingresar Capital, interés y cuotas',
	'msg.enterDetail': 'Debe indicar detalle.',
	'msg.expirationBeforeDate': 'La fecha de vencimiento no puede ser menor a la fecha seleccionada',
	'msg.fileUploaded': 'Archivo subido',
	'msg.firstExpiration': 'Primer Vencimiento',
	'msg.invalidAccount': 'Cuenta invalida',
	'msg.invalidContract': 'Alquiler invalido',
	'msg.invalidUserCount': 'Ingrese una cantidad de usuarios válida',
	'msg.invalidValue': 'Debe ingresar un valor válido',
	'msg.maxAvailableCapital': 'Capital máximo disponible',
	'msg.maximum': 'Máximo',
	'msg.nextExpirationTemplate': 'Próximo vencimiento {date}, cuota #{number} a generarse el {generationDate}',
	'msg.noContractAssigned': 'No tiene Contrato asignado',
	'msg.noData': 'No hay datos',
	'msg.noDebts': 'Sin deudas',
	'msg.noDebtorAssigned': 'No tiene Deudor asignado',
	'msg.noPendingCountDues': 'No hay cuotas pendientes de confirmación.',
	'msg.offSystemTooltip': 'Los datos que se completan aqui son únicamente informativos, con el fin de registrar la contabilidad fuera del sistema',
	'msg.paymentCreated': 'Cobro creado',
	'msg.paymentDeleted': 'Pago Eliminado',
	'msg.previousDuesWarning': 'Usted está por crear una deuda con fecha anterior a la fecha de hoy, si no desea crear cuotas previas debe ingresar una fecha igual o posterior al día de hoy',
	'msg.provisionalMonthlyAmount': 'Monto mensual provisorio',
	'msg.selectAccount': 'Debe seleccionar una cuenta.',
	'msg.selectAmount': 'Debe seleccionar monto.',
	'msg.selectDate': 'Debe seleccionar fecha.',
	'msg.selectDebtor': 'Debe seleccionar deudor.',
	'msg.selectExpiration': 'Debe seleccionar vencimiento.',
	'msg.selectFile': 'Debe seleccionar un archivo',
	'msg.selectPaid': 'Debe seleccionar pago.',
	'msg.selectSurchargeType': 'Debe seleccionar un tipo de recargo',
	'msg.surchargeApplied': 'Recargo aplicado correctamente',
	'msg.userCountConfirmed': 'Cantidad de usuarios confirmada',
	'msg.accountNotSelectedOrInvalid': 'Cuenta no seleccionada o invalida',
	'msg.addPaymentRequiredFields': 'Para ingresar pago debe completar los campos obligatorios: Pasividad,Cuota, Monto y Fecha',
	'msg.amountGreaterThanZero': 'Debe ingresar un monto mayor a 0',
	'msg.confirmDeleteCredit': 'Seguro desea eliminar pasividad? Si elimina pasividad, seran eliminados todos los cobros relacionados',
	'msg.confirmDeleteWarranty': 'Seguro desea eliminar garantia?',
	'msg.creditCreated': 'Pasividad creada',
	'msg.creditCreatedWithPreviousDues': 'Ésta crédito fue creado con fecha anterior, por lo que fueron generadas las cuotas previas a la fecha de creación',
	'msg.creditDeleted': 'Pasividad Eliminada',
	'msg.creditDuesLoadFailed': 'No se pudo cargar las cuotas de la pasividad',
	'msg.creditEditDatesTooltip': 'Estos datos solo se pueden editar luego de creado el credito mediante la opción:Cambiar fecha de inicio.',
	'msg.creditExpired': 'Pasividad vencida',
	'msg.creditPaymentCreated': 'Pago creado',
	'msg.creditPaymentUpdated': 'Pago actualizado correctamente',
	'msg.creditPaymentUpdatedCap': 'Pago actualizado Correctamente',
	'msg.creditStartDateTooltip': 'Es la fecha de inicio del crédito, si usted selecciona una fecha anterior al día de hoy se crearán las cuotas pasadas, si no es lo que desea seleccione una fecha igual o mayor a la de hoy.',
	'msg.creditStartDateUpdated': 'Fecha de inicio de credito actualizada correctamente ',
	'msg.creditUpdated': 'Pasividad actualizada Correctamente',
	'msg.editPaymentRequiredFields': 'Para editar pago debe completar los campos obligatorios: Monto y Fecha',
	'msg.enterCapitalDues': 'Debe ingresar Capital y cuotas',
	'msg.fileUploadedCap': 'Archivo Subido',
	'msg.invalidCredit': 'Pasividad invalida',
	'msg.invalidDebt': 'Deuda invalida',
	'msg.invalidDue': 'Cuota invalida',
	'msg.invalidShort': 'invalido',
	'msg.noLinkedCredit': 'No tiene Pasividad vinculada',
	'msg.paymentCreateFailed': 'Error al crear pago',
	'msg.paymentLoadFailed': 'No se pudo obtener pago',
	'msg.paymentUpdateFailed': 'Error al actualizar pago',
	'msg.previousDuesCreditWarning': 'Usted está por crear un crédito con fecha anterior a la fecha de hoy, si no desea crear cuotas previas debe ingresar una fecha igual o posterior al día de hoy',
	'msg.selectCredit': 'Debe seleccionar una pasividad.',
	'msg.selectCreditNoDot': 'Debe seleccionar una pasividad',
	'msg.selectDateFull': 'Debe seleccionar una fecha',
	'msg.selectDue': 'Debe seleccionar una cuota',
	'msg.selectFileShort': 'Seleccione Archivo',
	'msg.warrantyDeleted': 'Garantia Eliminada',

	// --- Extra page / section titles ---
	'title.accountingInfo': 'Información contable',
	'title.activity': 'Actividad',
	'title.billsEventual': 'Gastos eventuales',
	'title.billsPeriodic': 'Gastos periódicos',
	'title.changeHistory': 'Historial de cambios',
	'title.collections': 'Cobros',
	'title.confirmUserCount': 'Confirmar cantidad de usuarios',
	'title.debtWithPreviousDues': 'Deuda con cuotas previas',
	'title.deleteDebt': 'Eliminar Deuda',
	'title.deleteAccount': 'Eliminar Cuenta',
	'title.deleteContact': 'Eliminar Contacto',
	'title.deleteDocument': 'Eliminar Documento',
	'title.deleteUser': 'Eliminar Usuario',
	'title.deleteInterest': 'Eliminar Interés',
	'title.deletePayment': 'Eliminar Pago',
	'title.deleteCommission': 'Eliminar Comisión',
	'title.details': 'Detalles',
	'title.documents': 'Documentos',
	'title.attachments': 'Adjuntos',
	'title.reports': 'Informes',
	'title.dueGenerationData': 'Datos para la generación de cuotas',
	'title.editPayment': 'Editar Cobro',
	'title.interestList': 'Lista de Intereses',
	'title.newPayment': 'Nuevo Cobro',
	'title.offSystemRecord': 'Registro fuera del sistema',
	'title.paymentRegistered': 'Cobro registrado',
	'title.pendingCountDues': 'Cuotas por confirmar',
	'title.registerPayment': 'Registrar cobro',
	'title.changeHistoryLower': 'historial de cambios',
	'title.creditWithPreviousDues': 'Crédito con cuotas previas',
	'title.creditWithPreviousDuesPlain': 'Credito con cuotas previas',
	'title.deleteCredit': 'Eliminar Pasividad',
	'title.editStartDate': 'Editar Fecha de inicio',
	'title.newWarranty': 'Nueva Garantía',

	// --- Contracts area (chunk 3) ---
	'action.continue': 'Continuar',
	'action.goBack': 'Volver',
	'action.update': 'Actualizar',
	'field.collectionMethod': 'Forma de cobro',
	'field.housingUnit': 'Unidad',
	'field.pendingDue': 'Cuota pendiente',
	'field.previousBalanceDatePlain': 'Fecha saldo anterior',
	'field.previousBalanceLower': 'saldo Anterior',
	'field.responsible': 'Responsable',
	'form.hasPreviousBalance': 'Tiene saldo anterior?',
	'form.isRental': 'Es alquiler?',
	'form.paymentAccount': 'Cuenta para pago',
	'form.paymentTo': 'Pagar a',
	'form.placeholder.searchContractShort': 'Buscar alquiler...',
	'form.placeholder.searchProperty': 'Buscar una propiedad...',
	'form.placeholder.searchResponsibleUser': 'Buscar usuario responsable...',
	'form.placeholder.searchTenant': 'Buscar Inquilino... ',
	'msg.assetLoadFailed': 'No se pudo obtener activo/s',
	'msg.changeTenantTooltip': 'Para cambiar el inquilino debe finalizar el contrato, y generar uno nuevo a partir del actual',
	'msg.confirmDeleteDebtShort': 'Seguro desea eliminar la deuda?',
	'msg.contractCreated': 'Contrato creado',
	'msg.contractUpdated': 'Contrato actualizado',
	'msg.contractUpdatedCap': 'Contrato actualizado Correctamente',
	'msg.invalidProperty': 'Propiedad invalida',
	'msg.noContractsFound': 'No se encontraron alquileres',
	'msg.noContractsWithPendingDues': 'Sin alquileres con cuotas pendientes.',
	'msg.noPendingDuesForContract': 'Sin cuotas pendientes para este alquiler.',
	'msg.noPendingDuesFound': 'No se encontraron cuotas pendientes',
	'msg.noPropertyAssigned': 'No tiene Propiedad asignada',
	'msg.noResponsibleAssigned': 'No tiene Responsable asignado',
	'msg.noTenantAssigned': 'No tiene Inquilino asignado',
	'msg.notAssignedFem': 'No asignada',
	'msg.notSpecified': 'No especificado',
	'msg.rentalLoadFailed': 'No se pudo obtener el alquiler',
	'msg.selectProperty': 'Debe seleccionar una propiedad',
	'msg.selectTenantAccount': 'Debe seleccionar una cuenta como inquilino',
	'title.addPayment': 'Agregar cobro',
	'title.contractData': 'Datos Contractuales',
	'title.contractDataAlt': 'Datos Contracturales',

	// --- Assets / Bills / AssetSales / Detail screens (chunk 4) ---
	'entity.assetSentence': 'Activo fijo',
	'entity.assetSaleOf': 'Venta de activo',
	'title.saleTo': 'Venta a',
	'action.activatePeriodicity': 'Activar periodicidad',
	'action.addCollection': 'Agregar Cobro',
	'action.newDebt': 'Nueva Deuda',
	'action.confirmPayment': 'Confirmar Pago',
	'action.generateContract': 'Generar Contrato',
	'action.stopPeriodicity': 'Detener periodicidad',
	'action.viewEscritura': 'Ver Escritura',
	'action.viewReceiptLower': 'ver comprobante',
	'field.idShort': 'Id',
	'field.initialDelivery': 'Entrega Inicial',
	'field.period': 'Periodo',
	'field.descriptionPlain': 'Descripcion',
	'field.document': 'Documento',
	'field.sold': 'Vendido?',
	'field.escritura': 'Escritura',
	'field.ubicationPlain': 'Ubicacion',
	'field.billingDay': 'Dia de facturacion',
	'field.billResponsible': 'Responsable del gasto',
	'field.linkedAsset': 'Activo Vinculado',
	'field.linkedAssetLower': 'Activo vinculado',
	'field.paidFull': 'Pagado',
	'field.pendingPayment': 'Pendiente',
	'field.startDate': 'Fecha de inicio',
	'field.totalInDebts': 'Total en Deudas',
	'form.billDate': 'Fecha de gasto',
	'form.chooseAsset': 'Seleccione Activo',
	'form.closeDate': 'Fecha de finalización',
	'form.chooseOwner': 'Elige al Propietario',
	'form.generateRetroactive': 'Generar gastos retroactivos',
	'form.monthYear': 'Mes/año:',
	'form.multipleLotsQ': 'El activo tiene multiples padrones?',
	'form.partyType.tenant': 'Inquilino/Comprador',
	'form.partyType.owner': 'Propietario/Vendedor',
	// Display labels for stored enum values (the values themselves never change)
	'enum.partyType.inquilino': 'inquilino',
	'enum.partyType.propietario': 'propietario',
	'enum.paymentTo.cliente': 'Cliente',
	'enum.paymentTo.inmobiliaria': 'Inmobiliaria',
	'form.placeholder.billName': 'Nombre del gasto',
	'form.placeholder.searchAsset': 'Buscar Activo...',
	'form.placeholder.searchAssetSpace': 'Buscar Activo... ',
	'form.placeholder.searchBankAccount': 'Buscar cuenta bancaria...',
	'form.select': 'Seleccionar',
	'form.selectAsset': 'Seleccionar activo',
	'form.selectAssociatedAccount': 'Seleccionar cuenta asociada',
	'form.selectCurrency': 'Seleccionar moneda',
	'form.selectMonth': 'Seleccionar mes',
	'form.selectYear': 'Seleccionar año',
	'form.startMonth': 'Mes de inicio',
	'form.startYear': 'Año de inicio',
	'form.year': 'Año:',
	'form.placeholder.assetName': 'Nom Fe activo',
	'form.placeholder.assetUbication': 'Ubicación del activo',
	'form.placeholder.folderName': 'Nombre de la carpeta',
	'form.placeholder.lotNumber': 'Número de padrón',
	'form.placeholder.searchOwnerAccount': 'Buscar cuenta de propietario...',
	'form.placeholder.searchOwnerAccountCap': 'Buscar Cuenta de propietario... ',
	'msg.assetCreated': 'Activo creado',
	'msg.assetCreateFailed': 'Error al crear activo',
	'msg.assetDeleted': 'Activo Eliminado',
	'msg.assetEditFailed': 'Error al editar Activo',
	'msg.confirmDeleteAsset': 'Seguro desea eliminar el activo? Si elimina el activo, seran eliminados todos los gastos relacionados',
	'msg.assetUpdated': 'Activo actualizado Correctamente',
	'msg.fileUploadFailed': 'Error al subir el archivo',
	'msg.assetNotSelectedInvalid': 'Activo no seleccionado invalido',
	'msg.assetNotSelectedOrInvalid': 'Activo no seleccionado o invalido',
	'msg.assetSaleCreated': 'Venta de activo creada',
	'msg.assetSaleUpdated': 'Venta de activo actualizada',
	'msg.assetSaleDeleted': 'Venta Eliminada',
	'msg.assetSaleDeletedFull': 'Venta de activo Eliminada',
	'msg.assetSaleDeleteFailed': 'Error al eliminar venta',
	'msg.assetSaleDeleteFailedFull': 'Error al eliminar venta de activo',
	'msg.confirmDeleteAssetSale': 'Seguro desea eliminar la venta de activo? Si elimina la venta, serán eliminadas todas las deudas relacionadas',
	'msg.billCreated': 'Gasto creado',
	'msg.billDeleted': 'Gasto eliminado',
	'msg.billDeletedCap': 'Gasto Eliminado',
	'msg.billPeriodicityActivated': 'La periodicidad del gasto ha sido activada.',
	'msg.billPeriodicityStopped': 'La periodicidad del gasto ha sido detenida.',
	'msg.billUpdated': 'Gasto actualizado',
	'msg.confirmActivateBillPeriodicity': '¿Está seguro que desea activar la periodicidad de este gasto?',
	'msg.confirmStopBillPeriodicity': '¿Está seguro que desea detener la periodicidad de este gasto?',
	'msg.confirmedLower': 'confirmado',
	'msg.noLower': 'no',
	'msg.completeLotField': 'Debe completar el campo padrón.',
	'msg.confirmDeleteBill': 'Seguro desea eliminar el gasto?',
	'msg.closeContractWarning': 'La fecha seleccionada es menor a la actual, al confirmar ésta fecha de finalización se cancelaran todas las cuotas y los pagos vinculados, generados con posterioridad al mes seleccionado.',
	'msg.confirmDeleteContract': 'Seguro desea eliminar el contrato?',
	'msg.contractClosed': 'Contrato finalizado Correctamente',
	'msg.contractClosedWithDate': 'Contrato Finalizado con fecha',
	'msg.contractDeleted': 'Contrato Eliminado',
	'msg.selectAtLeastOneContract': 'Debe seleccionar al menos un alquiler',
	'msg.contractsAdjusted': 'Montos actualizados',
	'msg.contractsClosed': 'Alquileres finalizados',
	'msg.enterAdjustmentValue': 'Debe ingresar el valor del ajuste',
	'msg.enterCloseDate': 'Debe ingresar la fecha de finalización',
	'msg.creditorDeleteFailed': 'Error al eliminar acreedor',
	'msg.debtListLoadFailed': 'No se pudo obtener lista de deudas',
	'msg.documentAvailable': 'Documento disponible',
	'msg.fileUploadedSuccess': 'Archivo subido con exito',
	'msg.noAssetAssigned': 'No tiene Activo asignado',
	'msg.noDocument': 'No hay documento',
	'msg.noPurchaserAssigned': 'No tiene Comprador asignado',
	'msg.generateRetroactiveTooltip': 'Si está activo, se crean los gastos de meses anteriores (desde el mes de inicio hasta hoy). Si no, solo se crea el gasto del mes actual.',
	'msg.paymentConfirmed': 'Pago Confirmado',
	'msg.invalidAsset': 'Activo invalido',
	'msg.listLoadFailed': 'No se pudo obtener lista',
	'msg.stopFutureBillGeneration': 'Detener generacion de proximos gastos',
	'msg.multipleUnitsTooltip': 'Para multiples unidades de un mismo padrón, puede dejar vacio el campo y completarlo en la venta/alquiler',
	'msg.noOwnerAssigned': 'No tiene Propietario asignado',
	'msg.noRelatedAsset': 'No tiene Activo Relacionado',
	'msg.yesPlain': 'Si',
	'msg.selectAsset': 'Debe seleccionar un activo.',
	'msg.selectOwner': 'Debe seleccionar un propietario.',
	'msg.selectPeriod': 'Debe seleccionar un periodo.',
	'msg.selectPeriodicity': 'Debe seleccionar periodicidad.',
	'title.addAsset': 'Agregar Activo',
	'title.assetSale': 'Venta de activo',
	'title.balance': 'Balance',
	'title.closeContractWarning': 'Advertencia Finalizar Contrato',
	'title.commissions': 'Comisiones',
	'title.confirmCloseContract': 'Confirmar Finalizar Contrato',
	'title.deleteAsset': 'Eliminar Activo',
	'title.deleteAssetSale': 'Eliminar Venta',
	'title.deleteBill': 'Eliminar Gasto',
	'title.deleteContract': 'Eliminar Contrato',
	'title.editAsset': 'Editar Activo',
	'title.generatedBillsInfo': 'Informacion sobre gastos generados',
	'title.paymentList': 'Lista de Pagos',

	// Settings area (sidebar groups, items, landing page)
	'settings.title': 'Configuración',
	'settings.homeDescription': 'Administrá la configuración del sistema por área.',
	'settings.searchPlaceholder': 'Buscar en configuración...',
	'settings.noResults': 'Sin resultados.',
	'settings.backToApp': 'Volver al sistema',
	'settings.signOut': 'Cerrar sesión',
	'settings.group.company': 'Empresa',
	'settings.group.business': 'Negocio',
	'settings.group.accounting': 'Contabilidad',
	'settings.group.users': 'Usuarios',
	'settings.group.integrations': 'Integraciones',
	'settings.group.tools': 'Herramientas',
	'settings.group.terminology': 'Terminología',
	'settings.groupDesc.company': 'Identidad de la empresa, tema visual y emails de notificación.',
	'settings.groupDesc.business': 'Preset, conceptos, pestañas y dashboard.',
	'settings.groupDesc.terminology': 'Renombrá los textos de la aplicación por categoría.',
	'settings.groupDesc.accounting': 'IRPF, IVA, comisiones y cuentas bancarias de la empresa.',
	'settings.groupDesc.users': 'Alta, edición y roles de los usuarios del sistema.',
	'settings.groupDesc.integrations': 'Conexiones con servicios externos.',
	'settings.groupDesc.tools': 'Importación desde Excel, mantenimiento, auditoría y plantillas.',
	'settings.item.companyInfo': 'Información de la empresa',
	'settings.item.colors': 'Tema y colores',
	'settings.item.emails': 'Emails de notificación',
	'settings.item.preset': 'Preset de negocio',
	'settings.item.terminology': 'Terminología / Etiquetas',
	'settings.item.termEntities': 'Conceptos del negocio',
	'settings.item.termTitles': 'Títulos de pantalla',
	'settings.item.termFields': 'Campos y columnas',
	'settings.item.termForms': 'Formularios',
	'settings.item.termActions': 'Botones y acciones',
	'settings.item.termMessages': 'Mensajes',
	'settings.item.termAdmin': 'Panel de configuración',
	'settings.item.termDocuments': 'Balance y estado de cuenta',
	'settings.termEntitiesDescription':
		'Renombrar una entidad la actualiza en pestañas, títulos y tablas a la vez.',
	'settings.item.dashboard': 'Configuración del Dashboard',
	'settings.item.users': 'Usuarios',
	'settings.item.googleCalendar': 'Google Calendar',
	'settings.item.esignature': 'Firma electrónica',
	'settings.item.facturacion': 'Facturación electrónica',
	'settings.item.duplicateCleanup': 'Limpieza de cuotas duplicadas',
	'settings.item.auditTracking': 'Seguimiento de modificaciones',
	'settings.item.auditLog': 'Historial de cambios',
	'settings.cleanup.warning':
		'Herramienta de mantenimiento: elimina cuotas duplicadas de forma permanente. Ejecutá primero la vista previa y revisá el resultado antes de confirmar.',
	'settings.item.duesResync': 'Recálculo de estado de cuotas',
	'settings.duesResync.warning':
		'Herramienta de mantenimiento (solo super administrador): recalcula el estado pago/impago de las cuotas a partir de los cobros registrados y repara vínculos rotos. Ejecutá primero la vista previa y revisá el resultado antes de confirmar.',

	// Concept manager (create/edit concepts with behavior metadata)
	'concept.behaviorTitle': 'Comportamiento de los conceptos',
	'concept.behaviorDescription':
		'Cada concepto factura según un comportamiento del sistema. Crear un concepto nuevo lo habilita en los editores y en la generación automática de cuotas.',
	'concept.newConcept': 'Nuevo concepto',
	'concept.editTitle': 'Editar concepto',
	'concept.created': 'Concepto creado',
	'concept.updated': 'Concepto actualizado',
	'concept.nameRequired': 'El nombre del concepto es requerido',
	'concept.systemBadge': 'Sistema',
	'concept.systemBehaviorLocked':
		'Concepto del sistema: su comportamiento está protegido y no puede modificarse.',
	'concept.keyFixedNote': 'La clave no cambia al renombrar:',
	'concept.behaviorChangeTitle': 'Cambiar comportamiento',
	'concept.behaviorChangeWarning':
		'El cambio de comportamiento afecta solo la generación futura de cuotas; los registros existentes conservan el comportamiento con el que fueron creados.',
	'concept.commissionNote':
		'Opcional: porcentaje usado al registrar comisiones para este concepto (si se omite, se usa el % configurado en Contabilidad).',
	'concept.field.name': 'Nombre',
	'concept.field.key': 'Clave',
	'concept.field.behavior': 'Comportamiento',
	'concept.field.flags': 'Opciones',
	'concept.field.defaultPeriodicity': 'Periodicidad por defecto',
	'concept.field.commissionPercentage': '% Comisión',
	'concept.flag.requiresContract': 'Requiere contrato',
	'concept.flag.requiresAsset': 'Requiere activo',
	'concept.flag.requiresAssetSale': 'Requiere venta',
	'concept.flag.treatAsExpense': 'Es gasto',
	'concept.flag.ipcAdjustable': 'Ajusta por IPC',
	'concept.flag.allowsPerUser': 'Permite por usuario',
	'concept.behavior.periodic': 'Periódico',
	'concept.behavior.amortizable': 'Amortizable',
	'concept.behavior.interestOnly': 'Solo interés',
	'concept.behavior.installmentSale': 'Venta a plazo',
	'concept.behavior.deposit': 'Depósito / Garantía',
	'concept.behavior.accountDelivery': 'Entrega a cuenta',
	'concept.behavior.oneOff': 'Puntual (sin cuotas)',
	'concept.field.accrual': 'Devengamiento',
	'concept.field.arrearsDueDay': 'Día de vencimiento (mes siguiente)',
	'concept.accrual.advance': 'Por adelantado',
	'concept.accrual.arrears': 'Al cierre del mes',
	'concept.accrual.advanceNote':
		'La cuota del mes se genera el día 1 de ese mes y vence según la primera fecha de vencimiento del registro.',
	'concept.accrual.arrearsNote':
		'El mes se factura al cerrarse: la cuota del mes se genera su último día hábil, tanto en cobros como en pagos. La cuota vence el día indicado del mes siguiente (si cae en fin de semana, el lunes).',
	'concept.arrearsDueDayNote': 'Entre 1 y 28. Por defecto 15 (NET15).',

	// E-signature (DocuSeal integration)
	'esignature.send': 'Enviar a firmar',
	'esignature.refresh': 'Actualizar estado',
	'esignature.status.pending': 'Enviado a firmar',
	'esignature.status.completed': 'Firmado',
	'esignature.status.declined': 'Rechazado',
	'esignature.status.expired': 'Vencido',
	'esignature.modal.title': 'Enviar a firmar',
	'esignature.modal.selectOrCreateIntro':
		'Elegí una plantilla ya creada en DocuSeal para este documento, o creá una nueva a partir del archivo actual.',
	'esignature.modal.prepareButton': 'Crear plantilla nueva',
	'esignature.modal.templateInstructions':
		'En la pestaña de DocuSeal: agregá el segundo firmante si hace falta, asigná cada campo al firmante correcto y guardá la plantilla. Importante: no uses el botón "Send" ni compartas el enlace desde DocuSeal — volvé a esta ventana y continuá acá, para que el envío quede vinculado a este documento.',
	'esignature.modal.reopenLink': 'Volver a abrir DocuSeal',
	'esignature.modal.selectTemplate': 'Plantilla creada en DocuSeal',
	'esignature.modal.refreshTemplates': 'Actualizar lista',
	'esignature.modal.loading': 'Cargando...',
	'esignature.modal.signersDescription': 'Completá los datos de cada firmante.',
	'esignature.modal.signersRequired': 'Completá nombre y email de todos los firmantes',
	'esignature.modal.sent': 'Documento enviado a firmar',
	'esignature.modal.generatedPrompt': 'Documento generado. ¿Enviarlo a firmar ahora?',
	'esignature.modal.autoSignedHint': 'Se firmará automáticamente con la firma guardada de la empresa',
	'esignature.settings.statusTitle': 'Estado',
	'esignature.settings.defaultSignerTitle': 'Firmante por defecto',
	'esignature.settings.configured': 'Configurado',
	'esignature.settings.notConfigured': 'No configurado',
	'esignature.settings.reachable': 'DocuSeal accesible',
	'esignature.settings.unreachable': 'DocuSeal no responde',
	'esignature.settings.setupInstructions':
		'Configurá DOCUSEAL_URL, DOCUSEAL_API_KEY y DOCUSEAL_WEBHOOK_SECRET en las variables de entorno del servidor (ver docs/esignature.md).',
	'esignature.settings.defaultSignerName': 'Nombre del firmante de la empresa',
	'esignature.settings.defaultSignerEmail': 'Email del firmante de la empresa',
	'esignature.settings.saved': 'Configuración guardada',
	'esignature.settings.signatureImageTitle': 'Firma de la empresa',
	'esignature.settings.signatureImageHelp':
		'Subí una imagen de la firma de la empresa (PNG o JPG). Cuando esté configurada, el firmante de la empresa se marca como firmado automáticamente al enviar el documento — solo el cliente necesita firmar para completarlo.',
	'esignature.settings.uploadSignature': 'Subir firma',
	'esignature.settings.signatureSaved': 'Firma guardada',
	'esignature.settings.howItWorksTitle': 'Cómo funciona',
	'esignature.settings.howItWorksBody':
		'Al enviar un documento a firmar se abre DocuSeal para confirmar los campos de firma; luego ambas partes reciben un email con el enlace para firmar. El estado se actualiza automáticamente al completarse.',
	'esignature.settings.webhookInstructions':
		'Webhook: configurá en DocuSeal (Settings > Webhooks) la URL "<tu-app>/api/esignature/webhook" con el header X-Gota-Webhook-Secret y los eventos form.completed, form.declined, submission.completed, submission.expired.',
	'esignature.link.title': 'Vincular firma existente',
	'esignature.link.description':
		'Si el envío se hizo directamente desde DocuSeal (sin volver al asistente de este sistema), elegilo de la lista para vincularlo a este documento y traer su estado actual.',
	'esignature.link.empty': 'No se encontraron envíos recientes en DocuSeal',
	'esignature.link.template': 'Plantilla',
	'esignature.link.signers': 'Firmantes',
	'esignature.link.statusHeader': 'Estado',
	'esignature.link.linkButton': 'Vincular',
	'esignature.link.linked': 'Firma vinculada',

	// Facturación electrónica (GotaBilling / CFE DGI integration)
	'facturacion.facturar': 'Facturar',
	'facturacion.refresh': 'Actualizar estado',
	'facturacion.status.borrador': 'Borrador (sin emitir)',
	'facturacion.status.confirmado': 'Emitido',
	'facturacion.status.anulado': 'Anulado',
	'facturacion.tipoCfe.101': 'e-Ticket (101)',
	'facturacion.tipoCfe.111': 'e-Factura (111)',
	'facturacion.modal.title': 'Facturar balance',
	'facturacion.modal.intro':
		'Se creará un borrador de comprobante en GotaBilling con los ingresos del mes. La emisión (folio y CAE) se confirma después desde GotaBilling.',
	'facturacion.modal.receptor': 'Receptor',
	'facturacion.modal.descripcion': 'Descripción',
	'facturacion.modal.cantidad': 'Cantidad',
	'facturacion.modal.precioUnitario': 'Precio unitario',
	'facturacion.modal.total': 'Total',
	'facturacion.modal.confirm': 'Crear borrador',
	'facturacion.modal.sent': 'Borrador creado en GotaBilling',
	'facturacion.panel.title': 'Facturación electrónica',
	'facturacion.panel.serieNumero': 'Serie y número',
	'facturacion.panel.cae': 'CAE',
	'facturacion.panel.motivo': 'Motivo de anulación',
	'facturacion.panel.openDraft': 'Confirmar en GotaBilling',
	'facturacion.panel.pdf': 'Descargar PDF',
	'facturacion.panel.facturarNuevamente': 'Facturar nuevamente',
	'facturacion.panel.refreshed': 'Estado actualizado',
	'facturacion.iva.basica': 'IVA básica (22%)',
	'facturacion.iva.minima': 'IVA mínima (10%)',
	'facturacion.iva.exento': 'Exento de IVA',
	'facturacion.iva.default': 'Usar predeterminado de la empresa',
	'facturacion.formaPago.contado': 'Contado',
	'facturacion.formaPago.credito': 'Crédito',
	'facturacion.account.sectionTitle': 'Facturación electrónica',
	'facturacion.account.ivaOverride': 'IVA para facturación',
	'facturacion.account.ciudad': 'Ciudad (receptor CFE)',
	'facturacion.settings.statusTitle': 'Estado',
	'facturacion.settings.defaultsTitle': 'Valores por defecto',
	'facturacion.settings.configured': 'Configurado',
	'facturacion.settings.notConfigured': 'No configurado',
	'facturacion.settings.reachable': 'GotaBilling accesible',
	'facturacion.settings.unreachable': 'GotaBilling no responde',
	'facturacion.settings.testConnection': 'Probar conexión',
	'facturacion.settings.setupInstructions':
		'Configurá GOTABILLING_URL, GOTABILLING_API_KEY y GOTABILLING_WEBHOOK_SECRET en las variables de entorno del servidor (ver docs/facturacion.md). La API key se crea en GotaBilling, en Configuración > API.',
	'facturacion.settings.indicadorFacturacion': 'IVA por defecto',
	'facturacion.settings.montosBrutos': 'Los precios incluyen IVA (montos brutos)',
	'facturacion.settings.formaPago': 'Forma de pago por defecto',
	'facturacion.settings.saved': 'Configuración guardada',
	'facturacion.settings.howItWorksTitle': 'Cómo funciona',
	'facturacion.settings.howItWorksBody':
		'Desde la pestaña Balance de una cuenta, contrato o venta, el botón "Facturar" crea un borrador de CFE en GotaBilling con los ingresos del mes (las deducciones del balance no se incluyen). El folio y CAE se asignan al confirmar la emisión en GotaBilling, y el estado vuelve automáticamente por webhook.',
	'facturacion.settings.webhookInstructions':
		'Webhook: en GotaBilling, editá la API key y configurá como Webhook URL:',
	// --- Gotasoft ERP (flagged: SHOW_BILL_PAYERS / ESTIMATES / SOURCES / GOOGLE_CONNECTIONS / BANK_RECONCILIATION) ---
	'settings.item.billSources': 'Ingesta de facturas de proveedores',
	'settings.item.googleConnections': 'Conexiones Google',
	'settings.item.bankReconciliation': 'Conciliación bancaria',
	// SHOW_ATTRIBUTION — own entities and centros de atribución
	'settings.item.centers': 'Entidades y unidades de negocio',
	'attribution.pageDescription': 'Marcá qué cuentas son entidades propias y definí sus unidades de negocio (con su socio, si lo tiene). Contratos, liquidaciones y gastos se atribuyen a una unidad, por defecto la de su solución.',
	'billPayers.title': 'Pagadores',
	'billPayers.description': 'Cuentas que cubren un porcentaje de este gasto. Cada parte se registra como deuda en la cuenta del pagador; el resto queda a cargo del dueño del activo.',
	'billPayers.detailDescription': 'Cada parte es una deuda en la cuenta del pagador. Lo que no cubren los pagadores es costo del dueño del activo.',
	'billPayers.empty': 'Sin pagadores: el gasto se reparte como hasta ahora (dueño o inquilino).',
	'billPayers.add': 'Agregar pagador',
	'billPayers.selectAccount': 'Seleccionar cuenta',
	'billPayers.percentage': 'Porcentaje',
	'billPayers.total': 'Total asignado',
	'billPayers.ownerKeeps': 'Queda para el dueño',
	'billPayers.ownerShare': 'Dueño del activo',
	'billPayers.status': 'Estado',
	'billPayers.orphanDebts': 'Hay deudas de pagadores quitados del gasto que ya tienen pagos; se conservaron.',
	'billEstimates.amountMode': 'Tipo de monto',
	'billEstimates.mode.fixed': 'Fijo',
	'billEstimates.mode.estimated': 'Estimado (por consumo)',
	'billEstimates.modeHelp': 'Estimado: cada gasto generado usa el promedio de los últimos montos reales y queda marcado como estimado hasta registrar o recibir la factura real.',
	'billEstimates.estimated': 'Estimado',
	'billEstimates.setActual': 'Registrar monto real',
	'billEstimates.actualAmount': 'Monto real de la factura',
	'billEstimates.estimatedWas': 'Monto estimado',
	'billEstimates.invalidAmount': 'Ingrese un monto válido.',
	'billEstimates.saved': 'Monto real registrado',
	'billEstimates.source.gmail': 'Desde Gmail',
	'billEstimates.source.upload': 'Desde factura subida',
	'billEstimates.source.inbound': 'Desde correo reenviado',
	'billSources.pageDescription': 'Las facturas de proveedores (UTE, OSE, ANTEL, Neon, Heroku, …) completan el monto real de los gastos recurrentes y quedan adjuntas al gasto.',
	'billSources.inbox.title': 'Bandeja de facturas',
	'billSources.inbox.description': 'Facturas recibidas que necesitan revisión. Las que coinciden con una regla y traen monto se aplican solas.',
	'billSources.inbox.pending': 'Pendientes de revisión',
	'billSources.inbox.all': 'Todas',
	'billSources.inbox.empty': 'No hay facturas en esta vista.',
	'billSources.inbox.received': 'Recibida',
	'billSources.inbox.from': 'Remitente',
	'billSources.inbox.rule': 'Regla',
	'billSources.inbox.status': 'Estado',
	'billSources.inbox.assign': 'Asignar',
	'billSources.inbox.ignore': 'Ignorar',
	'billSources.inbox.assigned': 'Factura aplicada al gasto',
	'billSources.inbox.chooseBill': 'Seleccionar gasto',
	'billSources.status.applied': 'Aplicada',
	'billSources.status.needs_review': 'Revisar',
	'billSources.status.unmatched': 'Sin regla',
	'billSources.status.ignored': 'Ignorada',
	'billSources.status.error': 'Error',
	'billSources.source.gmail': 'Gmail',
	'billSources.source.upload': 'Subida manual',
	'billSources.source.inbound': 'Correo reenviado',
	'billSources.source.drive': 'Drive',
	'billSources.upload.title': 'Subir factura',
	'billSources.upload.description': 'Suba el PDF de una factura. Se busca la regla por el número de cuenta/servicio, o elija la regla.',
	'billSources.upload.autoRule': 'Detectar regla automáticamente',
	'billSources.upload.submit': 'Procesar factura',
	'billSources.upload.needFile': 'Seleccione un PDF',
	'billSources.upload.done': 'Factura procesada',
	'billSources.upload.duplicate': 'Esa factura ya había sido procesada',
	'billSources.rules.title': 'Reglas',
	'billSources.rules.description': 'Cada regla vincula un proveedor (remitente y, opcionalmente, número de cuenta) con una plantilla de gasto recurrente.',
	'billSources.rules.needTemplates': 'Primero cree un gasto periódico para vincular.',
	'billSources.rule.new': 'Nueva regla',
	'billSources.rule.edit': 'Editar regla',
	'billSources.rule.provider': 'Proveedor',
	'billSources.rule.custom': 'Personalizado',
	'billSources.rule.unverifiedPreset': 'Patrones sin verificar con una factura real: pruebe la regla con un PDF antes de usarla.',
	'billSources.rule.template': 'Gasto periódico',
	'billSources.rule.senders': 'Remitentes (separados por coma)',
	'billSources.rule.identifier': 'Nº de cuenta / servicio',
	'billSources.rule.periodOffset': 'Período facturado',
	'billSources.rule.offsetSame': 'Mes en que llega la factura',
	'billSources.rule.offsetPrevious': 'Mes anterior al que llega',
	'billSources.rule.advanced': 'Avanzado: expresiones de extracción',
	'billSources.rule.regexHelp': 'Expresiones regulares con un grupo de captura (sin distinguir mayúsculas). Use "Probar" para ver el texto de la factura y ajustarlas.',
	'billSources.rule.subjectPattern': 'Asunto (expresión opcional)',
	'billSources.rule.active': 'Regla activa',
	'billSources.rule.inactive': 'Inactiva',
	'billSources.rule.lastMatch': 'Última factura',
	'billSources.rule.saved': 'Regla guardada',
	'billSources.rule.confirmDelete': '¿Eliminar esta regla?',
	'billSources.parse.amountRegex': 'Monto',
	'billSources.parse.dueDateRegex': 'Vencimiento',
	'billSources.parse.periodRegex': 'Período',
	'billSources.parse.invoiceNumberRegex': 'Número de factura',
	'billSources.parse.identifierRegex': 'Nº de cuenta (extracción)',
	'billSources.parse.decimalSeparator': 'Formato de números',
	'billSources.test.title': 'Probar con una factura real',
	'billSources.test.help': 'No se guarda nada: muestra lo que la regla extraería y el texto del PDF.',
	'billSources.test.run': 'Probar',
	'billSources.test.needInput': 'Suba un PDF o pegue el texto de la factura',
	'billSources.test.pastePlaceholder': '…o pegue aquí el texto del correo',
	'billSources.forward.title': 'Correo reenviado (clientes)',
	'billSources.forward.body': 'Para clientes sin conexión a Gmail: un filtro de su correo reenvía las facturas a un servicio de correo entrante que las publica en esta dirección. Requiere la variable BILL_SOURCES_INBOUND_TOKEN.',
	'connections.pageDescription': 'Varias cuentas de Google (Gmail y Drive), cada una vinculada a la empresa cuyos registros alimenta.',
	'connections.setupInstructions': 'Faltan variables de entorno: GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET y CONNECTIONS_ENCRYPTION_KEY. Además, habilite las APIs de Gmail y Drive y registre /api/google-connections/callback como URI de redirección.',
	'connections.new.title': 'Conectar una cuenta',
	'connections.new.description': 'Solo se piden los permisos de los usos elegidos (lectura).',
	'connections.purpose.gmailInvoices': 'Gmail: facturas de proveedores',
	'connections.purpose.driveIssuedInvoices': 'Drive: facturas emitidas',
	'connections.owner': 'Empresa',
	'connections.noOwner': 'Sin empresa asignada',
	'connections.loginHint': 'Cuenta sugerida (opcional)',
	'connections.connect': 'Conectar con Google',
	'connections.connected': 'Cuenta conectada',
	'connections.connectError': 'No se pudo conectar la cuenta de Google',
	'connections.needPurpose': 'Elija al menos un uso',
	'connections.active': 'Sincronizando',
	'connections.inactive': 'Pausada',
	'connections.lastSync': 'Última sincronización',
	'connections.syncEnabled': 'Sincronizar automáticamente (cada 15 min)',
	'connections.gmailQuery': 'Búsqueda de Gmail',
	'connections.driveFolder': 'Carpeta de Drive (ID o enlace)',
	'connections.driveFolderPlaceholder': 'Pegue el enlace de la carpeta',
	'connections.saved': 'Conexión guardada',
	'connections.syncNow': 'Sincronizar ahora',
	'connections.synced': 'Sincronización completa',
	'connections.disconnect': 'Desconectar',
	'connections.confirmDelete': '¿Desconectar',
	'connections.issued.title': 'Facturas emitidas por revisar',
	'connections.issued.description': 'Facturas encontradas en Drive con la cuota de ingreso sugerida. Al confirmar, la factura queda vinculada a la cuota (el archivo sigue en Drive).',
	'connections.issued.empty': 'No hay facturas emitidas pendientes.',
	'connections.issued.score': 'coincidencia',
	'connections.issued.apply': 'Vincular',
	'connections.issued.applied': 'Factura vinculada a la cuota',
	'bankRec.pageDescription': 'Suba el estado de cuenta, revise las coincidencias propuestas y aplique solo las que confirme.',
	'bankRec.privacy': 'El estado de cuenta se procesa en memoria y no se guarda. Solo se registran las líneas que usted aplique (fecha y monto), nunca el detalle del banco.',
	'bankRec.upload.title': 'Estado de cuenta',
	'bankRec.upload.description': 'CSV o Excel exportado del banco (recomendado) o PDF de BBVA, Itaú u OCA.',
	'bankRec.file': 'Archivo',
	'bankRec.format': 'Formato',
	'bankRec.autoFormat': 'Detectar automáticamente',
	'bankRec.unverified': 'sin verificar',
	'bankRec.pdfCurrency': 'Moneda (PDF)',
	'bankRec.statementYear': 'Año del estado',
	'bankRec.analyze': 'Analizar',
	'bankRec.needFile': 'Seleccione el archivo',
	'bankRec.mapping.title': 'Columnas del archivo',
	'bankRec.mapping.description': 'Descripción',
	'bankRec.mapping.help': 'Indique qué columna es cada dato. Puede guardar el formato para los próximos meses.',
	'bankRec.mapping.date': 'Fecha',
	'bankRec.mapping.amount': 'Importe (con signo)',
	'bankRec.mapping.debit': 'Débito',
	'bankRec.mapping.credit': 'Crédito',
	'bankRec.mapping.currency': 'Moneda',
	'bankRec.mapping.dateFormat': 'Formato de fecha',
	'bankRec.mapping.defaultCurrency': 'Moneda por defecto',
	'bankRec.mapping.invertSign': 'Los egresos vienen en positivo (invertir signo)',
	'bankRec.mapping.saveAs': 'Guardar formato como',
	'bankRec.mapping.saveAsPlaceholder': 'Ej.: BBVA cuenta corriente',
	'bankRec.results.title': 'Movimientos',
	'bankRec.results.unverifiedProfile': 'Formato PDF sin verificar: controle que las líneas leídas coincidan con el estado de cuenta.',
	'bankRec.results.date': 'Fecha',
	'bankRec.results.description': 'Detalle',
	'bankRec.results.target': 'Aplicar a',
	'bankRec.results.apply': 'Aplicar',
	'bankRec.status.matched': 'Coincide',
	'bankRec.status.suggested': 'Sugerido',
	'bankRec.status.unmatched': 'Sin coincidencia',
	'bankRec.target.bill': 'Gasto',
	'bankRec.target.debtDue': 'Cobro',
	'bankRec.target.creditDue': 'Pago',
	'bankRec.target.newBill': 'Crear gasto pagado',
	'bankRec.remember': 'Recordar',
	'bankRec.noCandidates': 'Sin coincidencias',
	'bankRec.applySelected': 'Aplicar seleccionadas',
	'bankRec.applyHelp': 'Solo se registran las líneas marcadas.',
	'bankRec.applied': 'Líneas aplicadas',
	'bankRec.applyPartial': 'Algunas líneas no se aplicaron',
	'bankRec.nothingSelected': 'No hay líneas seleccionadas',
	'bankRec.incompleteSelection': 'Hay líneas marcadas sin destino (o sin activo para crear el gasto).',
	'settings.item.aiAccess': 'Agentes de IA',
	'aiAccess.pageDescription': 'Conectá tu asistente de IA (Claude, etc.) a gotaccount para consultar y actualizar datos conversando.',
	'aiAccess.explainer': 'El agente que use un token actúa como el usuario dueño del token: ve y puede hacer exactamente lo mismo que ese usuario, nada más. Todo lo que haga queda registrado a su nombre. Antes de modificar algo, el agente muestra una vista previa y espera confirmación.',
	'aiAccess.new.title': 'Nuevo token',
	'aiAccess.expiry': 'Vence',
	'aiAccess.days30': 'En 30 días',
	'aiAccess.days90': 'En 90 días',
	'aiAccess.days365': 'En 1 año',
	'aiAccess.noExpiry': 'Sin vencimiento',
	'aiAccess.create': 'Crear token',
	'aiAccess.showOnce': 'Copiá el token ahora: no se vuelve a mostrar.',
	'aiAccess.copy': 'Copiar',
	'aiAccess.copied': 'Copiado',
	'aiAccess.list.title': 'Mis tokens',
	'aiAccess.endpoint': 'Dirección del servidor MCP',
	'aiAccess.empty': 'Todavía no creaste tokens.',
	'aiAccess.created': 'Creado',
	'aiAccess.lastUsed': 'Último uso',
	'aiAccess.expires': 'Vence',
	'aiAccess.revoke': 'Revocar',
	'aiAccess.revoked': 'Revocado',
	'aiAccess.assistant': 'Asistente',
	'aiAccess.forUser': 'Para el usuario',
	'aiAccess.me': 'yo',
	'aiAccess.soon': 'próximamente',
	'aiAccess.oauthPending': 'Este asistente se conecta con inicio de sesión (OAuth), que todavía no está disponible. Por ahora podés usar Claude Code, Gemini CLI, Cursor o VS Code.',
	'aiAccess.issueFor': 'El token será de',
	'aiAccess.issueForTail': ': el agente tendrá sus permisos, no los tuyos.',
	'aiAccess.handOver': 'Entregáselo por un canal seguro a',
	'aiAccess.keepSecret': 'Quien tenga este token puede actuar como ese usuario: no lo compartas en chats, capturas ni repositorios. Si se expone, revocalo y creá otro.',
	'aiAccess.scopeMine': 'Mis tokens',
	'aiAccess.scopeManaged': 'Todos los que administro',
	'aiAccess.user': 'Usuario',
	'aiAccess.issuedBy': 'creado por',
	'aiAccess.confirmRevoke': '¿Revocar este token? El agente que lo use pierde el acceso de inmediato.',

	// Excel importer (Activos > Importar gastos)
	'import.billsPageTitle': 'Importar gastos',
	'import.billsPageDescription':
		'Cargá gastos de activos desde un archivo Excel. Las columnas se reconocen por las etiquetas configuradas en Terminología y podés ajustarlas a mano antes de importar.',
	'import.entity.debtPayments': 'Cobros de deudas',
	'import.entity.creditPayments': 'Pagos de pasividades',
	'import.field.associatedAccount': 'Cuenta asociada',
	'import.field.partyType': 'A cargo de',
	'import.field.paymentDate': 'Fecha de pago',
	'import.field.contactName': 'Contacto principal',
	'import.field.expirationDay': 'Día de vencimiento',
	'import.field.bankAccount': 'Cuenta bancaria',
	'import.field.ctaCte': 'Cta Cte',
	'import.field.location': 'Ubicación',
	'import.lookup.assetText': 'Texto libre (nombre, Padrón o ubicación)',
	'import.lookup.accountNumber': 'Número de cuenta',
	'import.lookup.bank': 'Banco',
	'import.lookup.customId': 'ID (customId)',
	'import.lookup.internalId': 'ID interno',
	'import.lookup.accountName': 'Nombre de la cuenta',
	'import.lookup.accountRut': 'RUT de la cuenta',
	'import.lookup.accountDoc': 'Nro. de documento de la cuenta',
	'import.lookup.assetLot': 'Padrón del activo',
	'import.lookup.assetName': 'Nombre del activo',
	'import.lookup.parentCustomId': 'ID de la deuda/pasividad',
	'import.due.month': 'Cuota del mes',
	'import.due.oldestUnpaid': 'Cuota impaga más antigua',
	'import.due.customId': 'ID de la cuota (columna Cuota)',
	'import.step.file': 'Archivo',
	'import.step.mapping': 'Columnas',
	'import.step.review': 'Revisión',
	'import.step.result': 'Resultado',
	'import.entityQuestion': '¿Qué querés importar?',
	'import.fileHelp': 'Arrastrá un archivo .xlsx, .xls o .csv, o hacé clic para elegirlo.',
	'import.sheet': 'Hoja',
	'import.rowsFound': 'filas con datos',
	'import.tooManyRows': 'El archivo tiene {count} filas; el máximo por importación es {max}. Dividilo en varios archivos.',
	'import.mappingHelp':
		'Elegí qué columna del Excel corresponde a cada campo (los nombres de columna se toman de la primera fila). Las columnas se reconocen automáticamente por las etiquetas configuradas en Terminología. El Excel no tiene IDs: para los campos que apuntan a otro registro, indicá en «Contiene» qué dato trae la columna (por ejemplo, el Padrón del activo).',
	'import.ignoreColumn': '(no importar)',
	'import.fillLater': 'Llenar en próximo paso',
	'import.fieldColumn': 'Dato de {entity}',
	'import.excelColumn': 'Columna de Excel',
	'import.autoMap': 'Mapear automáticamente',
	'import.missingRequired': 'Faltan columnas para los campos requeridos:',
	'import.linkColumn': 'Columna',
	'import.lookupColumn': 'Contiene',
	'import.fallback.assetOwner': 'Sin columna: se usa el dueño del activo',
	'import.fallback.expirationDay': 'Sin columna: día de «{column}» + mes y año que elegís en el próximo paso',
	'import.dueStrategy': '¿Cómo elegir la cuota?',
	'import.monthSource': 'Mes tomado de',
	'import.monthSource.date': 'la fecha del pago',
	'import.monthSource.period': 'la columna Período',
	'import.conceptHint': 'Si una cuenta o activo tiene varias deudas, mapeá la columna Concepto para distinguirlas.',
	'import.accountMatch': 'Actualizar cuentas existentes que coincidan por',
	'import.accountMatchHelp': 'Si ninguna opción está marcada, todas las filas se crean como cuentas nuevas.',
	'import.defaultAccountType': 'Tipo para filas sin tipo',
	'import.notifyBills': 'Enviar email de notificación por cada gasto importado',
	'import.registerPayment': 'Marcar el gasto como pagado cuando la fila tiene Fecha de pago',
	'import.fillableHint': 'Sin columna (se completan en la revisión):',
	'import.group.incomplete': 'Incompletos',
	'import.optional': 'opcional',
	'import.suggested': 'Sugeridos',
	'import.searchRecord': 'Buscar...',
	'import.noResults': 'Sin resultados',
	'import.unresolved.title': 'Valores sin identificar',
	'import.unresolved.help': 'Estos valores del archivo no coinciden con ningún registro. Elegí el que corresponde: se aplica a todas las filas con ese valor.',
	'import.unresolved.rows': 'fila(s)',
	'import.dateParts.day': 'Día',
	'import.dateParts.month': 'Mes',
	'import.dateParts.year': 'Año',
	'import.monthYear': 'Fecha de {field} para todas las filas',
	'import.monthYearHelp': 'Día, mes y año se copian a todas las filas; si el archivo trae «Día de vto», ese día se mantiene. Podés editar cada fila.',
	'import.rowsTable.title': 'Completar valores faltantes',
	'import.rowsTable.help': 'Completá en cada fila los datos que faltan y volvé a validar. Las filas completas pasan a Nuevos.',
	'import.pendingFills': 'Hay datos completados sin validar.',
	'import.validate': 'Validar',
	'import.validating': 'Validando...',
	'import.group.new': 'Nuevos',
	'import.group.existing': 'Con diferencias',
	'import.group.unchanged': 'Sin cambios',
	'import.group.duplicate': 'Posibles duplicados',
	'import.group.invalid': 'Con errores',
	'import.group.all': 'Todos',
	'import.row': 'Fila',
	'import.status': 'Estado',
	'import.issues': 'Observaciones',
	'import.keepCurrent': 'Mantener actual',
	'import.useFile': 'Usar archivo',
	'import.allUseFile': 'Todo desde el archivo',
	'import.allKeep': 'Mantener todo',
	'import.currentValue': 'Actual',
	'import.finalValue': 'Valor final',
	'import.toCreate': 'a crear',
	'import.toUpdate': 'a actualizar',
	'import.apply': 'Importar',
	'import.importing': 'Importando...',
	'import.created': 'Creados',
	'import.updated': 'Actualizados',
	'import.failed': 'Fallidos',
	'import.warnings': 'Advertencias',
	'import.downloadErrors': 'Descargar errores',
	'import.reviewAgain': 'Volver a validar',
	'import.startOver': 'Nueva importación',
	'import.result.ok': 'Importación completada.',
	'import.result.partial': 'Importación parcial: algunas filas no se pudieron importar.',
	'import.result.failed': 'No se importó ninguna fila.',
	'import.invisibleChars': 'Tenía caracteres invisibles (se eliminaron)',
	'import.back': 'Atrás',
	'import.next': 'Siguiente',
};

// Nav tab schema -> terminology key. A terminology override on the mapped key
// takes precedence over the ObjectTab label so renaming an entity renames its tab too.
export const NAV_LABEL_KEYS = {
	dashboard: 'nav.dashboard',
	accounts: 'entity.account.plural',
	debts: 'entity.debt.plural',
	credits: 'entity.credit.plural',
	contracts: 'entity.contract.plural',
	assets: 'entity.asset.plural',
	assetSales: 'entity.assetSale.plural',
	reports: 'nav.reports',
	predictions: 'nav.predictions',
	bills: 'entity.bill.plural',
};
